// ============================================
// StatCard Component
// KPI stat card with icon, label, value, and badge
// Supports two variants: large (Inventory) and compact (Products)
// ============================================

import { ReactNode } from 'react';
import { cn } from '../../utils/cn';

// ============================================
// Types
// ============================================

type StatCardVariant = 'large' | 'compact';

interface StatCardProps {
  /** Card variant - 'large' for Inventory page, 'compact' for Products page */
  variant?: StatCardVariant;
  /** Icon component or element */
  icon: ReactNode;
  /** Background color class for icon container */
  iconBg?: string;
  /** Main label text */
  label: string;
  /** Main value text */
  value: string | number;
  /** Value color override (e.g., for low stock alerts) */
  valueColor?: string;
  /** Optional badge element */
  badge?: ReactNode;
  /** Additional className */
  className?: string;
}

// ============================================
// Component
// ============================================

export function StatCard({
  variant = 'large',
  icon,
  iconBg = 'bg-blue-50',
  label,
  value,
  valueColor,
  badge,
  className,
}: StatCardProps) {
  const isLarge = variant === 'large';

  return (
    <div
      className={cn(
        'rounded-xl bg-white shadow-sm',
        isLarge
          ? 'p-3'
          : 'flex items-center gap-4 border border-slate-200 p-3',
        className
      )}
      style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}
    >
      {isLarge ? (
        // Large variant (Inventory page style)
        <>
          <div className="mb-4 flex items-start justify-between">
            {/* Icon */}
            <div
              className={cn(
                'flex h-12 w-12 items-center justify-center rounded-xl',
                iconBg
              )}
            >
              {icon}
            </div>
            {/* Badge */}
            {badge && <div>{badge}</div>}
          </div>
          {/* Label */}
          <p className="mb-1 text-sm font-medium text-slate-500">{label}</p>
          {/* Value */}
          <p
            className={cn(
              'font-sans text-xl font-bold leading-8 text-[#014361] sm:text-[32px] sm:leading-[40px]',
              valueColor
            )}
          >
            {value}
          </p>
        </>
      ) : (
        // Compact variant (Products page style)
        <>
          {/* Icon */}
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
              iconBg
            )}
          >
            {icon}
          </div>
          {/* Content */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] font-bold uppercase tracking-[1px] text-slate-400">
                {label}
              </p>
              {badge && <div>{badge}</div>}
            </div>
            <p
              className={cn(
                'text-xl font-extrabold text-[#014361]',
                valueColor
              )}
            >
              {value}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================
// Badge Sub-component
// ============================================

type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

interface StatBadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
}

const badgeStyles: Record<BadgeVariant, string> = {
  success: 'bg-emerald-50 text-emerald-600',
  warning: 'bg-amber-50 text-amber-600',
  danger: 'bg-red-50 text-red-600',
  info: 'bg-blue-50 text-primary-600',
  neutral: 'bg-slate-100 text-slate-500',
};

export function StatBadge({ children, variant = 'neutral' }: StatBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold',
        badgeStyles[variant]
      )}
    >
      {children}
    </span>
  );
}
